import React, { FunctionComponent, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { closeDialog } from '../../../app/dialog/dialog.actions';
import { useSelectWithParams } from '../../../utils/selector.utils';
import { dialogSelectors } from '../../../app/dialog/dialog.selectors';
import { meetingSimpleSelectors } from '../../../app/resource/meeting-simple/meeting-simple.selectors';
import { ConnectedDialog } from '../ConnectedDialog';
import { ISendMessageToMembersDialogProps } from './types';
import { UserSelector } from './UserSelector';
import { IUser } from '../../../app/resource/user/user.types';

export const SendMessageToMembersDialog: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    message: '',
    members: '',
  });

  const dialogProps = useSelectWithParams(
    dialogSelectors.selectDialogProps,
    'sendMessageToMembersDialog',
  ) as ISendMessageToMembersDialogProps | null;

  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, dialogProps?.meetingId || '');

  const [selectedUserIds, setSelectedUserIds] = useState<IUser['id'][]>([]);

  const initialShareWithIds = meeting?.shareWithIds ?? [];
  useEffect(() => {
    if (initialShareWithIds.length === 0) {
      return;
    }
    setSelectedUserIds((prev) => (!prev || prev.length === 0 ? (initialShareWithIds as IUser['id'][]) : prev));
  }, [initialShareWithIds]);

  const validateForm = useCallback(() => {
    const errors = {
      message: '',
      members: '',
    };

    if (!message.trim()) {
      errors.message = 'Message is required';
    }

    if (selectedUserIds.length === 0) {
      errors.members = 'Please select at least one member';
    }

    setValidationErrors(errors);
    return !errors.message && !errors.members;
  }, [message, selectedUserIds]);

  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newMessage = e.target.value;
      setMessage(newMessage);

      if (validationErrors.message && newMessage.trim()) {
        setValidationErrors((prev) => ({ ...prev, message: '' }));
      }
    },
    [validationErrors.message],
  );

  const handleMembersChange = useCallback(
    (newSelectedUserIds: string[]) => {
      setSelectedUserIds(newSelectedUserIds);

      if (validationErrors.members && newSelectedUserIds.length > 0) {
        setValidationErrors((prev) => ({ ...prev, members: '' }));
      }
    },
    [validationErrors.members],
  );

  const handleClose = useCallback(() => {
    dispatch(closeDialog('sendMessageToMembersDialog'));
  }, [dispatch]);

  const handleSend = useCallback(() => {
    if (validateForm() && dialogProps?.onSend) {
      dialogProps.onSend(message.trim(), selectedUserIds);
      setMessage('');
      setSelectedUserIds([]);
      handleClose();
    }
  }, [validateForm, dialogProps, message, selectedUserIds, handleClose]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <ConnectedDialog dialogKey="sendMessageToMembersDialog">
      <div className="popup new_team_popup mid-popup isVisible">
        <div className="panel" style={{ minWidth: '500px' }}>
          <div className="panel-header small-header flex jcsb">
            <h3>Send Message to Members</h3>
            <span className="close-popup close" onClick={handleClose}>
              <i className="icon-close"></i>
            </span>
          </div>
          <div className="panel-body">
            <div className="form-group mb-2">
              <label htmlFor="message">Members</label>
              {meeting && (
                <UserSelector
                  userIds={meeting?.shareWithIds}
                  selectedUserIds={selectedUserIds}
                  onSelectionChange={handleMembersChange}
                  title="Select Members"
                />
              )}
              {validationErrors.members && (
                <div className="error-message" style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
                  {validationErrors.members}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                className={`form-control my-1 ${validationErrors.message ? 'error' : ''}`}
                rows={6}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                placeholder="Type your message here..."
                autoFocus
                style={validationErrors.message ? { borderColor: '#dc3545' } : {}}
              />
              {validationErrors.message && (
                <div className="error-message" style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
                  {validationErrors.message}
                </div>
              )}
            </div>
            <div className="panel-footer no-border flex jcr">
              <button className="btn btn-border white-bg close mr-1" onClick={handleClose}>
                Cancel
              </button>
              <button className="btn btn-green white" onClick={handleSend}>
                Send Message
              </button>
            </div>
          </div>
        </div>
      </div>
    </ConnectedDialog>
  );
};
